import { useMemo, useState } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { useFilterDraft } from '@/hooks/useFilterDraft';
import { parseEnumFilter, parseNumberFilter } from '@/utils/filters';
import { FEEDBACK_STATUS } from '../constants/feedback.constants';
import type { FeedbackFilters } from '../types/filters.types';
import type { FeedbackListParams } from '../types/feedback.types';

export function useFeedbackListFilters() {
  const [searchText, setSearchText] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<FeedbackFilters>({});
  const [filterOpen, setFilterOpen] = useState(false);

  const { draftValues, setDraftValues, applyValues, hasPendingChanges } = useFilterDraft({
    appliedValues: appliedFilters,
    onApply: setAppliedFilters,
    shouldWaitForApply: true,
  });

  const debouncedSearchText = useDebounce(searchText);

  const queryParams = useMemo<FeedbackListParams>(
    () => ({
      search: debouncedSearchText.trim() || undefined,
      startDate: appliedFilters.startDate || undefined,
      endDate: appliedFilters.endDate || undefined,
      typeId: parseNumberFilter(appliedFilters.typeId),
      status: parseEnumFilter(appliedFilters.status, FEEDBACK_STATUS),
    }),
    [debouncedSearchText, appliedFilters],
  );

  const activeFilterCount = Object.values(appliedFilters).filter(Boolean).length;

  return {
    searchText,
    filters: draftValues,
    filterOpen,
    setFilterOpen,
    activeFilterCount,
    hasPendingChanges,
    queryParams,
    handleSearchChange: setSearchText,
    handleFilterChange: setDraftValues,
    applyFilterValues: applyValues,
  };
}
